/**
 * Client-side image preparation for admin uploads.
 *
 * The server already downscales everything to 1600px WebP (see
 * server/src/services/image.service.ts), so shipping multi-megabyte phone
 * originals over the wire is pure waste — and a batch of them blows past
 * Nginx's client_max_body_size, which surfaces as an opaque 413 before the
 * request ever reaches Node. Downscaling here keeps a 6-image batch in the
 * low single-digit megabytes.
 */

/** Longest edge we keep, leaving headroom above the server's 1600px target. */
const MAX_DIMENSION = 2000;
const WEBP_QUALITY = 0.85;
const JPEG_QUALITY = 0.9;

/** Files already this small aren't worth re-encoding. */
const SKIP_COMPRESSION_BELOW = 600 * 1024;

/** Multer's per-file cap (server/src/routes/admin.products.routes.ts). */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Budget for one multipart request. Deliberately below the Nginx
 * client_max_body_size in nginx/*.conf so we warn before Nginx 413s.
 */
export const MAX_BATCH_BYTES = 10 * 1024 * 1024;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

let webpSupport: boolean | null = null;

function supportsWebp(): boolean {
  if (webpSupport !== null) return webpSupport;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  return webpSupport;
}

/**
 * Decode honouring EXIF orientation — without this, portrait phone photos
 * come out rotated once the canvas strips the EXIF tag that sharp's
 * .rotate() would otherwise have read.
 */
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('decode failed'));
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

/**
 * Downscale and re-encode one image. Returns the original file untouched if
 * it's already small, if the browser can't decode it, or if the re-encode
 * somehow came out bigger.
 */
export async function compressImage(file: File): Promise<File> {
  if (file.size <= SKIP_COMPRESSION_BELOW) return file;

  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await decode(file);
  } catch {
    return file;
  }

  const srcWidth = source.width;
  const srcHeight = source.height;
  const scale = Math.min(1, MAX_DIMENSION / Math.max(srcWidth, srcHeight));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(srcWidth * scale);
  canvas.height = Math.round(srcHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if ('close' in source) source.close();

  const useWebp = supportsWebp();
  const type = useWebp ? 'image/webp' : 'image/jpeg';
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, type, useWebp ? WEBP_QUALITY : JPEG_QUALITY);
  });
  if (!blob || blob.size >= file.size) return file;

  const ext = useWebp ? 'webp' : 'jpg';
  const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'imagem';
  return new File([blob], `${baseName}.${ext}`, { type, lastModified: file.lastModified });
}
